import { type Action } from 'redux';
import { type ThunkDispatch } from 'redux-thunk';

import { Jwt } from '@pkg/classes';
import { flushTokens } from '@pkg/hooks';

import { callTokenRefresh } from '@app/client/resources/w3auth/w3auth';
import { setError } from '@app/store/actions/error';
import { setLoader } from '@app/store/actions/loader';
import { initializeUser } from '@app/store/actions/user';
import { type RootState } from '@app/store/deferred-redux-store';
import { timestampAlert } from '@app/store/slices';
import { LoaderType } from '@app/store/types/loader';
import { setTokens, useAuthTokens } from '@app/store/zustand/auth/useAuthTokens';

export async function refreshAuthToken(dispatch: ThunkDispatch<RootState, void, Action>) {
    const tokens = useAuthTokens.getState().token;

    const jwtAccess = new Jwt(tokens?.access_token);
    if (!jwtAccess.isExpired()) {
        await dispatch(initializeUser());
        return;
    }

    // Call API to refresh token
    const jwtRefresh = new Jwt(tokens?.refresh_token);

    if (!jwtRefresh.isExpired()) {
        const r = await callTokenRefresh({ value: { refresh_token: jwtRefresh.jwtString || '' } });
        // The loader is opened here and closed in `getCurrentUserEmojis`
        dispatch(setLoader({ open: true, type: LoaderType.YatV2Loader }));
        try {
            if (r.ok) {
                setTokens(r.value, { keepPrevious: true });
                useAuthTokens.getState().setAuthTokens(r.value);

                if (r.value.requires_2fa && jwtRefresh.is2FAExpired()) {
                    return;
                }
                await dispatch(initializeUser());
            } else {
                dispatch(setError({ error: r, notify: true }));
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            dispatch(setError({ ...e, notify: true }));
            flushTokens();
        } finally {
            dispatch(setLoader({ open: false }));
            dispatch(timestampAlert());
        }
    }
}
